/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

// Getting list of clients from provisioning service
const requestClientsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedClientsSuccess.create(response);
};

const requestClientsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedClientsError.create(error);
};

export const requestClientsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestClients.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/clients`).pipe(
          map((response: AjaxResponse<any>): Action => requestClientsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestClientsError(error))),
          ),
        ),
    ),
  );

// Request Current Client
const receivedCurrentClientSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCurrentClientSuccess.create(response);
};

const receivedCurrentClientError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCurrentClientError.create(error);
};

export const requestCurrentClientEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCurrentClient.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/clients/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => receivedCurrentClientSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(receivedCurrentClientError(error))),
          ),
        ),
    ),
  );

// Creating a client
const clientProvisioningSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ClientProvisioningSuccess.create(response);
};

const clientProvisioningError = (error: AjaxError): any => {
  return ActionCreators.ClientProvisioningError.create(error);
};

export const provisionClientEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ProvisionClient.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/provisioning/clients', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => clientProvisioningSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(clientProvisioningError(error))),
          ),
        ),
    ),
  );

// Delete a client
const deleteClientSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeleteClientSuccess.create(response);
};

const deleteClientError = (error: AjaxError): any => {
  return ActionCreators.DeleteClientError.create(error);
};

export const deleteClientEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteClient.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/provisioning/clients/${$action.payload}`).pipe(
          switchMap(
            (response: AjaxResponse<any>): Array<Action> => [
              deleteClientSuccess(response),
              ActionCreators.RequestClients.create(),
            ],
          ),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(deleteClientError(error))),
          ),
        ),
    ),
  );

// Update a client
const updateClientSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateClientSuccess.create(response);
};

const updateClientError = (error: AjaxError): any => {
  return ActionCreators.UpdateClientError.create(error);
};

export const updateClientEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateClient.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.put(`/provisioning/clients/${$action.payload.id}`).pipe(
          map((response: AjaxResponse<any>): Action => updateClientSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(updateClientError(error))),
          ),
        ),
    ),
  );
